import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, setUnauthorizedHandler, type StreamData } from "./api";
import { buildTree, type TreeNode } from "./tree";
import { TreeNav } from "./TreeNav";
import { GridView } from "./GridView";
import { LiveDashboard } from "./LiveDashboard";
import { StreamCard } from "./StreamCard";
import { SearchPanel } from "./SearchPanel";
import { MetricsBar } from "./MetricsBar";
import { Sidebar } from "./Sidebar";
import { useSSE } from "./useSSE";
import { useTreeStats } from "./useTreeStats";

type ViewMode = { type: "dashboard" } | { type: "live" } | { type: "grid"; node: TreeNode } | { type: "product"; node: TreeNode } | { type: "stream"; key: string };

export function App() {
    // Global SSE subscription — drives all cache invalidation
    useSSE();

    const [treeCollapsed, setTreeCollapsed] = useState(false);
    const [view, setView] = useState<ViewMode>({ type: "dashboard" });
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [playingKey, setPlayingKey] = useState<string | null>(null);
    const [treeSelectedKey, setTreeSelectedKey] = useState<string | null>(null);
    const streamRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Auth failures used to render as silently-empty lists ("no live streams")
    // because 401 bodies failed JSON parsing inside each query. Surface them.
    const [unauthorized, setUnauthorized] = useState(false);
    useEffect(() => {
        setUnauthorizedHandler(() => setUnauthorized(true));
        return () => setUnauthorizedHandler(null);
    }, []);

    const { data: streams = [] } = useQuery({
        queryKey: ["streams"],
        queryFn: api.streams,
        staleTime: 30_000,
        // Slow background poll as safety net; SSE is primary update driver
        refetchInterval: 120_000,
    });

    const { data: products = [] } = useQuery({
        queryKey: ["products"],
        queryFn: api.products,
        staleTime: 300_000,
    });

    const { data: events = [] } = useQuery({
        queryKey: ["events"],
        queryFn: api.events,
        staleTime: 60_000,
        refetchInterval: 120_000,
    });

    const tree = useMemo(() => buildTree(streams, products, events), [streams, products, events]);
    const treeStatsMap = useTreeStats(tree);

    function findNode(key: string, nodes: TreeNode[] = tree): TreeNode | null {
        for (const n of nodes) {
            if (n.key === key) return n;
            const found = findNode(key, n.children);
            if (found) return found;
        }
        return null;
    }

    const handleTreeSelect = (key: string, type: "year" | "product" | "day" | "stream") => {
        setTreeSelectedKey(key);
        if (type === "stream") {
            setView({ type: "stream", key });
            setExpandedKey(key);
            setTimeout(() => streamRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        } else {
            const node = findNode(key);
            if (node) setView({ type: "grid", node });
        }
    };

    const toggle = (key: string) => {
        if (expandedKey === key) { setExpandedKey(null); setPlayingKey(null); }
        else setExpandedKey(key);
    };

    // Jump to a search hit: open + play the stream, then seek the player once it mounts.
    const handleSearchJump = (key: string, seconds: number) => {
        setView({ type: "stream", key });
        setExpandedKey(key);
        setPlayingKey(key);
        setTreeSelectedKey(key);
        setTimeout(() => streamRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        // Player mounts asynchronously; poll briefly for the <video> then seek.
        let tries = 0;
        const seek = () => {
            const v = document.getElementById("playerVideo") as HTMLVideoElement | null;
            if (v && v.readyState >= 1) { v.currentTime = seconds; v.play().catch(() => {}); }
            else if (tries++ < 40) setTimeout(seek, 150);
        };
        setTimeout(seek, 200);
    };

    const openStream = (key: string) => {
        setView({ type: "stream", key });
        setExpandedKey(key);
        setTreeSelectedKey(key);
    };

    const liveCount = streams.filter(s => s.status === "active" && s.type !== "dvr").length;

    // Resolve display based on view mode
    let displayStreams: StreamData[] = [];
    let viewTitle = "Dashboard";
    let showGrid = false;
    let gridNode: TreeNode | null = null;

    if (view.type === "dashboard") {
        gridNode = tree.length === 1 ? tree[0] : { label: "All Streams", key: "all", children: tree, streams };
        displayStreams = streams;
        viewTitle = "All Streams";
        showGrid = true;
    } else if (view.type === "grid" || view.type === "product") {
        gridNode = view.node;
        displayStreams = view.node.streams || [];
        viewTitle = view.node.label;
        showGrid = true;
    } else if (view.type === "stream") {
        const s = streams.find(s => s.stream_key === view.key);
        displayStreams = s ? [s] : [];
        viewTitle = s?.title || view.key;
    }

    return (
        <div className="app-layout">
            {unauthorized && (
                <div className="auth-banner">
                    Not signed in — open the dashboard using your full link with <code>?token=…</code> to authorize this browser.
                </div>
            )}
            <TreeNav
                tree={tree}
                selectedKey={treeSelectedKey}
                onSelect={handleTreeSelect}
                collapsed={treeCollapsed}
                onToggleCollapse={() => setTreeCollapsed(!treeCollapsed)}
                statsMap={treeStatsMap}
                liveCount={liveCount}
                liveSelected={view.type === "live"}
                onSelectLive={() => { setView({ type: "live" }); setTreeSelectedKey(null); }}
            />
            <div className="app-main">
                <h1>feistv archive</h1>
                <MetricsBar />
                <SearchPanel onJump={handleSearchJump} />

                {view.type === "live" ? (
                    <LiveDashboard streams={streams} onSelectStream={openStream} />
                ) : showGrid && gridNode ? (
                    <GridView node={gridNode} onSelectStream={openStream} />
                ) : (
                    <div className="streams">
                        {displayStreams.length === 0 && <div className="empty">no streams</div>}
                        {displayStreams.map(s => (
                            <div key={s.stream_key} ref={el => { streamRefs.current[s.stream_key] = el; }}>
                                <StreamCard
                                    stream={s}
                                    expanded={s.stream_key === expandedKey}
                                    playing={s.stream_key === playingKey}
                                    onToggle={() => toggle(s.stream_key)}
                                    onPlay={() => setPlayingKey(playingKey === s.stream_key ? null : s.stream_key)}
                                    onStop={() => api.stop(s.stream_key)}
                                    onRetry={() => api.retry(s.stream_key)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Sidebar />
        </div>
    );
}
